import {
  getPatientAnnouncementBannerRuleValue,
  isPatientAnnouncementBannerValueVisible,
  PATIENT_ANNOUNCEMENT_BANNER_COLORS,
  PATIENT_ANNOUNCEMENT_BANNER_ICONS,
  PATIENT_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH,
  PATIENT_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH,
  PATIENT_ANNOUNCEMENT_BANNER_PRESETS,
  patientAnnouncementBannerSchema,
  type PatientAnnouncementBanner,
  type PatientAnnouncementBannerColor,
  type PatientAnnouncementBannerConfigResponse,
  type PatientAnnouncementBannerIcon,
  type PatientAnnouncementBannerRule,
} from '@dev-console/patient-announcement-banner';
import { Pod } from '@shared/pods/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { BannerPreview } from '@/components/patient-banner/banner-preview';
import { ConfirmBannerDialog } from '@/components/patient-banner/confirm-banner-dialog';
import { ErrorBanner, LoadingCard } from '@/components/ui/state-views';
import { getPatientAnnouncementBanner, updatePatientAnnouncementBanner } from '@/config/api-helpers';
import { useAuth } from '@/lib/auth-context';

export const meta = {
  label: 'Patient Banner',
  order: 7,
};

const QUERY_KEY = ['patient-announcement-banner'];
const INCIDENT_LEAD_GROUP = 'SG-Eng Incident Lead';

type BannerDraft = {
  title: string;
  text: string;
  color: PatientAnnouncementBannerColor;
  icon: PatientAnnouncementBannerIcon;
};

const EMPTY_DRAFT: BannerDraft = { title: '', text: '', color: 'error', icon: 'information-circle' };

type PendingChange = { rule: PatientAnnouncementBannerRule; banner: PatientAnnouncementBanner | null };

// Mirrors assertCanUpdatePatientAnnouncementBanner in the server controller; the server remains the
// enforcement point.
function canUpdateBanner(user: { pods?: string[]; groups?: string[] } | null): boolean {
  if (user == null) return false;
  const pods = user.pods ?? [];
  return (
    pods.includes(Pod.CONSUMER) || pods.includes(Pod.PLATFORM) || (user.groups ?? []).includes(INCIDENT_LEAD_GROUP)
  );
}

function pickDefaultRule(rules: PatientAnnouncementBannerRule[]): PatientAnnouncementBannerRule | undefined {
  return (
    rules.find((rule) => rule.environments?.includes('production')) ??
    rules.find((rule) => /prod/i.test(rule.name)) ??
    rules[0]
  );
}

function toDraft(value: Record<string, unknown>): BannerDraft {
  const color = PATIENT_ANNOUNCEMENT_BANNER_COLORS.find((option) => option === value.color);
  const icon = PATIENT_ANNOUNCEMENT_BANNER_ICONS.find((option) => option === value.icon);
  return {
    title: typeof value.title === 'string' ? value.title : '',
    text: typeof value.text === 'string' ? value.text : '',
    color: color ?? EMPTY_DRAFT.color,
    icon: icon ?? EMPTY_DRAFT.icon,
  };
}

function formatEnvironments(rule: PatientAnnouncementBannerRule): string {
  return rule.environments == null || rule.environments.length === 0
    ? 'all environments'
    : rule.environments.join(', ');
}

function SegmentButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type='button'
      aria-pressed={active}
      onClick={onClick}
      className={`h-8 rounded px-3 text-sm transition-colors ${
        active
          ? 'bg-neutral-900 text-white'
          : 'border border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400'
      }`}
    >
      {label}
    </button>
  );
}

function LiveStatus({
  config,
  rule,
  canUpdate,
  onEdit,
  onTurnOff,
}: {
  config: PatientAnnouncementBannerConfigResponse;
  rule: PatientAnnouncementBannerRule;
  canUpdate: boolean;
  onEdit: (value: Record<string, unknown>) => void;
  onTurnOff: () => void;
}) {
  const liveValue = getPatientAnnouncementBannerRuleValue(rule);
  const isLive = isPatientAnnouncementBannerValueVisible(liveValue);
  const isSplit = liveValue === undefined;
  const canTurnOff =
    isLive ||
    (isSplit &&
      rule.variants.some(
        (variant) => variant.passPercentage > 0 && isPatientAnnouncementBannerValueVisible(variant.value)
      ));

  return (
    <div className='space-y-4 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <h3 className='font-mediumbold text-base text-neutral-900'>Live now</h3>
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              isLive ? 'bg-red-100 text-red-800' : 'bg-neutral-100 text-neutral-600'
            }`}
          >
            {isSplit ? 'Split across variants' : isLive ? 'Banner showing' : 'No banner'}
          </span>
        </div>
        {canTurnOff && (
          <button
            type='button'
            disabled={!canUpdate}
            onClick={onTurnOff}
            className='h-8 rounded-md bg-red-600 px-3 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500'
          >
            Turn off banner
          </button>
        )}
      </div>

      {!config.isEnabled && (
        <div className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
          This dynamic config is disabled in Statsig, so patients get the default value regardless of this rule.
        </div>
      )}
      {rule.passPercentage < 100 && (
        <div className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
          This rule only passes {rule.passPercentage}% of matching patients. Raise it in Statsig to reach everyone.
        </div>
      )}

      {isLive && liveValue != null && <BannerPreview value={liveValue} />}

      {rule.variants.length > 0 && (
        <table className='min-w-full divide-y divide-neutral-100 text-left text-sm'>
          <thead className='text-neutral-500'>
            <tr>
              <th className='py-2 pr-3 font-medium'>Statsig variant</th>
              <th className='py-2 pr-3 font-medium'>Split</th>
              <th className='py-2 pr-3 font-medium'>Text</th>
              <th className='py-2 font-medium' />
            </tr>
          </thead>
          <tbody className='divide-y divide-neutral-100'>
            {rule.variants.map((variant) => (
              <tr key={variant.id ?? variant.name} className='align-top'>
                <td className='py-2 pr-3 text-neutral-800'>{variant.name}</td>
                <td className='py-2 pr-3 text-neutral-700'>{variant.passPercentage}%</td>
                <td className='max-w-[480px] py-2 pr-3 text-neutral-600'>
                  {typeof variant.value?.text === 'string' && variant.value.text !== '' ? (
                    variant.value.text
                  ) : (
                    <span className='text-neutral-400'>No banner</span>
                  )}
                </td>
                <td className='py-2 text-right'>
                  {isPatientAnnouncementBannerValueVisible(variant.value) && variant.value != null && (
                    <button
                      type='button'
                      onClick={() => onEdit(variant.value ?? {})}
                      className='h-7 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700 hover:border-neutral-400'
                    >
                      Use copy
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className='text-xs text-neutral-500'>
        Rule <span className='font-medium text-neutral-700'>{rule.name}</span> · {formatEnvironments(rule)}
        {config.lastModifiedTime != null && (
          <>
            {' '}
            · Last modified {new Date(config.lastModifiedTime).toLocaleString()}
            {config.lastModifierName != null && ` by ${config.lastModifierName}`}
          </>
        )}
      </div>
    </div>
  );
}

function BannerComposer({
  draft,
  canUpdate,
  onChange,
  onSubmit,
}: {
  draft: BannerDraft;
  canUpdate: boolean;
  onChange: (draft: BannerDraft) => void;
  onSubmit: (banner: PatientAnnouncementBanner) => void;
}) {
  const parsed = patientAnnouncementBannerSchema.safeParse(draft);

  return (
    <div className='space-y-4 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm'>
      <h3 className='font-mediumbold text-base text-neutral-900'>New banner</h3>

      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-sm text-neutral-500'>Examples</span>
        {PATIENT_ANNOUNCEMENT_BANNER_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type='button'
            onClick={() => onChange(toDraft(preset.banner))}
            className='h-8 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 hover:border-neutral-400'
          >
            {preset.label}
          </button>
        ))}
      </div>

      <label className='block space-y-1'>
        <span className='text-sm font-medium text-neutral-700'>Title (optional)</span>
        <input
          type='text'
          value={draft.title}
          maxLength={PATIENT_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH}
          onChange={(event) => onChange({ ...draft, title: event.target.value })}
          className='h-9 w-full rounded-md border border-neutral-200 px-3 text-sm focus:border-neutral-400 focus:outline-none'
        />
      </label>

      <label className='block space-y-1'>
        <span className='flex justify-between text-sm font-medium text-neutral-700'>
          Message
          <span className='font-normal text-neutral-400'>
            {draft.text.length}/{PATIENT_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH}
          </span>
        </span>
        <textarea
          value={draft.text}
          maxLength={PATIENT_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH}
          onChange={(event) => onChange({ ...draft, text: event.target.value })}
          rows={3}
          className='w-full rounded-md border border-neutral-200 p-3 text-sm focus:border-neutral-400 focus:outline-none'
        />
      </label>

      <div className='flex flex-wrap gap-6'>
        <div className='space-y-1'>
          <span className='text-sm font-medium text-neutral-700'>Color</span>
          <div className='flex flex-wrap gap-2'>
            {PATIENT_ANNOUNCEMENT_BANNER_COLORS.map((color) => (
              <SegmentButton
                key={color}
                active={draft.color === color}
                label={color}
                onClick={() => onChange({ ...draft, color })}
              />
            ))}
          </div>
        </div>
        <label className='space-y-1'>
          <span className='block text-sm font-medium text-neutral-700'>Icon</span>
          <select
            value={draft.icon}
            onChange={(event) =>
              onChange({
                ...draft,
                icon: PATIENT_ANNOUNCEMENT_BANNER_ICONS.find((icon) => icon === event.target.value) ?? EMPTY_DRAFT.icon,
              })
            }
            className='h-8 rounded-md border border-neutral-200 bg-white px-2 text-sm'
          >
            {PATIENT_ANNOUNCEMENT_BANNER_ICONS.map((icon) => (
              <option key={icon} value={icon}>
                {icon}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className='space-y-1'>
        <span className='text-sm font-medium text-neutral-700'>Preview</span>
        <BannerPreview value={draft} />
      </div>

      <div className='flex flex-wrap items-center justify-end gap-3'>
        {!canUpdate && (
          <span className='text-sm text-neutral-500'>
            Requires the Consumer pod, Platform pod, or SG-Eng Incident Lead.
          </span>
        )}
        <button
          type='button'
          onClick={() => onChange(EMPTY_DRAFT)}
          className='h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 hover:border-neutral-400'
        >
          Clear form
        </button>
        <button
          type='button'
          disabled={!canUpdate || !parsed.success}
          onClick={() => parsed.success && onSubmit(parsed.data)}
          className='h-9 rounded-md bg-neutral-900 px-4 text-sm font-medium text-white hover:bg-neutral-700 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500'
        >
          Show banner
        </button>
      </div>
    </div>
  );
}

export function PatientBannerPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canUpdate = canUpdateBanner(user);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [draft, setDraft] = useState<BannerDraft>(EMPTY_DRAFT);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);

  const {
    data: config,
    error,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getPatientAnnouncementBanner,
  });

  const updateMutation = useMutation({
    mutationFn: updatePatientAnnouncementBanner,
    onSuccess: ({ config: updated }) => {
      if (updated == null) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      } else {
        queryClient.setQueryData(QUERY_KEY, updated);
      }
      setPendingChange(null);
    },
    onError: () => setPendingChange(null),
  });

  const rules = config?.rules ?? [];
  const selectedRule = rules.find((rule) => rule.id === selectedRuleId) ?? pickDefaultRule(rules);

  return (
    <div className='max-w-4xl space-y-5'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h2 className='font-mediumbold text-xl text-neutral-900'>Patient Banner</h2>
          <div className='mt-1 text-sm text-neutral-500'>
            Announcement banner on the patient app home and chat screens, served by the{' '}
            <span className='font-mono text-xs'>patient_announcement_banner</span> Statsig config.
          </div>
        </div>
        <div className='flex items-center gap-3'>
          {config != null && (
            <a
              className='text-sm text-blue-700 hover:underline'
              href={config.statsigUrl}
              rel='noreferrer'
              target='_blank'
            >
              Open in Statsig
            </a>
          )}
          <button
            type='button'
            disabled={isFetching}
            onClick={() => void refetch()}
            className='h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 shadow-sm hover:border-neutral-400 disabled:opacity-50'
          >
            Refresh
          </button>
        </div>
      </div>

      {updateMutation.error != null && (
        <ErrorBanner
          title='Could not update the banner'
          message={updateMutation.error instanceof Error ? updateMutation.error.message : 'Update failed'}
        />
      )}

      {isLoading && <LoadingCard message='Loading patient banner config...' />}

      {error != null && (
        <ErrorBanner
          message={error instanceof Error ? error.message : 'Failed to load patient banner config'}
          onRetry={refetch}
        />
      )}

      {config != null && selectedRule == null && (
        <div className='rounded-lg border border-neutral-200 bg-white px-4 py-6 text-sm text-neutral-600'>
          This config has no rules to update. Add a rule in Statsig first.
        </div>
      )}

      {config != null && selectedRule != null && (
        <>
          {rules.length > 1 && (
            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-sm text-neutral-500'>Rule</span>
              {rules.map((rule) => (
                <SegmentButton
                  key={rule.id}
                  active={rule.id === selectedRule.id}
                  label={`${rule.name} (${formatEnvironments(rule)})`}
                  onClick={() => setSelectedRuleId(rule.id)}
                />
              ))}
            </div>
          )}

          <LiveStatus
            config={config}
            rule={selectedRule}
            canUpdate={canUpdate}
            onEdit={(value) => setDraft(toDraft(value))}
            onTurnOff={() => setPendingChange({ rule: selectedRule, banner: null })}
          />

          <BannerComposer
            draft={draft}
            canUpdate={canUpdate}
            onChange={setDraft}
            onSubmit={(banner) => setPendingChange({ rule: selectedRule, banner })}
          />
        </>
      )}

      {pendingChange != null && (
        <ConfirmBannerDialog
          rule={pendingChange.rule}
          banner={pendingChange.banner}
          isSaving={updateMutation.isPending}
          onCancel={() => setPendingChange(null)}
          onConfirm={() => {
            updateMutation.reset();
            updateMutation.mutate({ ruleId: pendingChange.rule.id, banner: pendingChange.banner });
          }}
        />
      )}
    </div>
  );
}
